/** Tag filter: "all" or a model's primary browse tag (tags[0]). */
export type TagFilter = "all" | string;

/** Human-readable chip label for a manifest tag. */
export function formatTagLabel(tag: string): string {
  return tag.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeTag(tag: string): string | null {
  const trimmed = tag.trim();
  return trimmed.length > 0 ? trimmed.toLowerCase() : null;
}

/** Primary browse tag — convention: tags[0] is the chip category, rest are search keywords. */
export function primaryTag(
  model: { tags: readonly string[] },
): string | null {
  const first = model.tags[0];
  if (!first) return null;
  const key = normalizeTag(first);
  return key ? first.trim() : null;
}

/**
 * Unique primary tags for browse chips, sorted by model count (desc) then A–Z.
 * Descriptor tags (tags[1+]) are excluded — use search to find those.
 */
export function collectBrowseTags(
  models: ReadonlyArray<{ tags: readonly string[] }>,
): string[] {
  const counts = countModelsByPrimaryTag(models);
  const canonical = new Map<string, string>();
  for (const model of models) {
    const tag = primaryTag(model);
    const key = tag ? normalizeTag(tag) : null;
    if (key && !canonical.has(key)) {
      canonical.set(key, tag!);
    }
  }
  return [...canonical.values()].sort((a, b) => {
    const countDiff =
      (counts.get(normalizeTag(b)!) ?? 0) - (counts.get(normalizeTag(a)!) ?? 0);
    if (countDiff !== 0) return countDiff;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  });
}

/** Model counts per primary tag (case-insensitive). */
export function countModelsByPrimaryTag(
  models: ReadonlyArray<{ tags: readonly string[] }>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const model of models) {
    const tag = primaryTag(model);
    const key = tag ? normalizeTag(tag) : null;
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/** Whether a model's primary tag matches (case-insensitive). */
export function modelMatchesBrowseTag(
  model: { tags: readonly string[] },
  tag: string,
): boolean {
  const key = normalizeTag(tag);
  if (!key) return false;
  const primary = primaryTag(model);
  return primary !== null && normalizeTag(primary) === key;
}
